import { useState } from 'react';
import type { ReasoningEvidenceSnapshotDto } from '../types/active-reasoning';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  listInvestigationReconciliations,
  recordInvestigationReconciliation,
  type ReconciliationCandidateDto,
  type RelationshipDecisionDto,
} from '../api/investigations.api';

interface Props {
  investigationId: string;
  reasoningVersion: number | null;
  canReview: boolean;
  evidenceSnapshot: ReasoningEvidenceSnapshotDto;
  onOpenEvidenceReference?: (reference: string) => void;
}

const options: { value: RelationshipDecisionDto; label: string }[] = [
  { value: 'SAME_QUESTION', label: 'Same causal question' },
  { value: 'DISTINCT_QUESTIONS', label: 'Distinct causal questions' },
  { value: 'INSUFFICIENT_INFORMATION', label: 'Insufficient information' },
];

export function HumanReconciliationReview({ investigationId, reasoningVersion, canReview, evidenceSnapshot, onOpenEvidenceReference }: Props) {
  const queryClient = useQueryClient();
  const queryKey = ['human-reconciliations', investigationId, reasoningVersion];
  const reviews = useQuery({
    queryKey,
    queryFn: () => listInvestigationReconciliations(investigationId),
    enabled: Boolean(investigationId && reasoningVersion),
  });
  const [editing, setEditing] = useState<string | null>(null);
  const [choice, setChoice] = useState<RelationshipDecisionDto>('INSUFFICIENT_INFORMATION');
  const [rationale, setRationale] = useState('');
  const evidenceDescriptions = (snapshot: ReasoningEvidenceSnapshotDto, ref: string): string => {
    const source = [
      ...snapshot.investigation_evidence, ...snapshot.documents, ...snapshot.messages,
      ...snapshot.findings, ...snapshot.human_answers, ...snapshot.observations,
    ].find((item) => item.evidence_reference === ref);
    const location = source?.source_location;
    const excerpt = [location?.['text'], location?.['note']].find(
      (item): item is string => typeof item === 'string' && item.trim().length > 0,
    );
    return excerpt ? excerpt.replace(/\s+/g, ' ').slice(0, 160) : `${source?.source_type ?? 'Evidence'} · ${ref}`;
  };
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!reasoningVersion || reviews.isPending) return null;
  if (reviews.isError) return <div className="r42-reconciliation-error" role="alert">Relationship reviews are temporarily unavailable.</div>;
  const data = reviews.data;
  if (!data || (!data.candidates.length && !data.decisions.length)) return null;

  async function submit(candidate: ReconciliationCandidateDto) {
    if (!data?.reviewable || !canReview || !reasoningVersion) return;
    setSaving(true);
    setError(null);
    try {
      await recordInvestigationReconciliation(investigationId, {
        expected_reasoning_version: reasoningVersion,
        uncertainty_id: candidate.uncertainty_id,
        causal_hypothesis_id: candidate.causal_hypothesis_id,
        decision: choice,
        rationale: rationale.trim(),
        evidence_refs: candidate.shared_evidence_refs,
      });
      await queryClient.invalidateQueries({ queryKey });
      setEditing(null);
      setRationale('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the review. Reload and try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <details className="r42-human-reconciliation" open={data.candidates.length > 0}>
      <summary><span>Relationship review</span><strong>{data.candidates.length} to review</strong></summary>
      <div className="r42-human-reconciliation__content">
        <p className="r42-human-reconciliation__intro">Two findings may refer to the same causal question. A reviewer can clarify their relationship without changing the evidence or automatically confirming a cause.</p>
        {!data.reviewable ? <p role="status">Reasoning has changed or is still processing. Reviews are read-only until reasoning is current.</p> : null}
        {data.candidates.map((candidate) => {
          const id = `${candidate.uncertainty_id}:${candidate.causal_hypothesis_id}`;
          return <article key={id} className="r42-human-reconciliation__candidate">
            <div><strong>Unresolved question</strong><p>{candidate.uncertainty_statement}</p></div>
            <div><strong>Supported causal explanation</strong><p>{candidate.causal_hypothesis_statement}</p></div>
            <div className="r42-human-reconciliation__evidence"><strong>Shared source evidence</strong>{candidate.shared_evidence_refs.map((ref, index) => onOpenEvidenceReference ? (
              <button key={ref} type="button" onClick={() => onOpenEvidenceReference(ref)} title={ref}>Source {index + 1}: {evidenceDescriptions(evidenceSnapshot, ref)}</button>
            ) : <span key={ref}>Source {index + 1}: {evidenceDescriptions(evidenceSnapshot, ref)}</span>)}</div>
            {canReview && data.reviewable ? (
              editing === id ? <form onSubmit={(event) => { event.preventDefault(); void submit(candidate); }}>
                <label>Relationship decision
                  <select value={choice} onChange={(event) => setChoice(event.target.value as RelationshipDecisionDto)}>{options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}</select>
                </label>
                <label>Rationale
                  <textarea rows={3} maxLength={2000} minLength={10} required value={rationale} onChange={(event) => setRationale(event.target.value)} placeholder="Explain how these questions are related (or why they are distinct)." />
                </label>
                <p className="r42-human-reconciliation__disclaimer">Your review records a relationship, not new causal proof. If you mark the same question, a subsequent reasoning refresh may resolve the duplicate inquiry only while the original supporting evidence remains valid. Machine Memory is unchanged.</p>
                <div className="r42-human-reconciliation__actions">
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setEditing(null); setError(null); }} disabled={saving}>Cancel</button>
                  <button type="submit" className="btn btn-sm r42-btn-primary" disabled={saving || rationale.trim().length < 10}>{saving ? 'Saving…' : 'Record decision'}</button>
                </div>
              </form> : <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setEditing(id); setChoice('INSUFFICIENT_INFORMATION'); setRationale(''); setError(null); }}>Review relationship</button>
            ) : null}
          </article>;
        })}
        {data.decisions.map((decision) => <article className="r42-human-reconciliation__decision" key={decision.uncertainty_id}>
          <strong>Reviewed · {options.find((opt) => opt.value === decision.decision)?.label}</strong>
          <p>{decision.rationale}</p>
          <small>Reasoning version {decision.reasoning_version} · Relationship recorded. Refresh reasoning to evaluate its effect; this is not causal proof.</small>
        </article>)}
        {error ? <p className="r42-reconciliation-error" role="alert">{error}</p> : null}
      </div>
    </details>
  );
}
